/**
 * Base class for image services
 * Services are instantiated per URL and contain all the logic for that URL
 */
export class ImageService {
    /**
     * Create a service instance for the given URL
     * Subclasses should override this to implement URL detection and conversion
     *
     * @param {string} _url - Image URL
     * @param {Object} _context - Context (e.g., sharedLibraryStatic, sharedLibraryDis)
     * @returns {ImageService|null} Service instance or null if URL not supported
     */
    static create(_url, _context) {
        throw new Error('create() must be implemented by subclass')
    }

    /**
     * Constructor - stores URL and base info
     * @param {string} url - The service-ready URL (converted/validated URL that can be used with this service).
     *                       For services that convert URLs (e.g., DisService), this is the converted URL.
     *                       For services that don't convert (e.g., BrightcoveService), this is the original validated URL.
     * @param {Object} baseInfo - Service-specific base information extracted from URL
     * @param {string} [fallbackUrl] - Optional direct URL to use if service is down
     */
    constructor(url, baseInfo, fallbackUrl = null) {
        this.url = url
        this.baseInfo = baseInfo
        this.fallbackUrl = fallbackUrl
    }

    /**
     * Get fallback URL for when service is unavailable
     * @returns {string|null} Direct URL that bypasses the service, or null
     */
    getFallbackUrl() {
        return this.fallbackUrl
    }

    /**
     * Get supported formats for this source
     * @returns {Array<string>} Array of MIME types
     */
    getSupportedFormats() {
        throw new Error('getSupportedFormats() must be implemented')
    }

    /**
     * Generate all source elements for responsive images
     * @param {Array<number>} _sizes - Array of pixel widths to generate (from bucketing logic)
     * @returns {Array<{type: string, srcSet: string}>} Array of source props
     *
     * @example
     * service.generateSources([200, 400, 800, 1600])
     * // Returns: [
     * //   {type: 'image/avif', srcSet: 'url1 200w, url2 400w, ...'},
     * //   {type: 'image/webp', srcSet: 'url1 200w, url2 400w, ...'},
     * //   {type: 'image/jpeg', srcSet: 'url1 200w, url2 400w, ...'}
     * // ]
     */
    generateSources(_sizes) {
        throw new Error('generateSources() must be implemented')
    }

    /**
     * Generate responsive URL for given width
     * @param {number} _width - Target width in pixels
     * @param {Object} [_options] - Optional service-specific options (e.g., targetFormat for DIS)
     * @returns {string|null} Generated URL or null if invalid
     */
    generateUrl(_width, _options = {}) {
        throw new Error('generateUrl() must be implemented')
    }

    /**
     * Extract width from a service-specific URL
     * Each service has its own URL format for specifying width (e.g., query param, path segment)
     * @param {string} _url - Image URL in this service's format
     * @returns {number|null} Width in pixels or null if not found/invalid
     */
    extractWidthFromUrl(_url) {
        throw new Error('extractWidthFromUrl() must be implemented')
    }
}
